import React from 'react';
import classnames from 'classnames';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Carousel, FrameBone, ParagraphBone, ProductTile, ProductTileDirection, SvgIcon } from '@gik/ui';
import { Product } from '@gik/core/models/gik/Product';
import twConfig from '@/../tailwind.config';
import ProductTileToolbar from '@gik/ui/gik/ProductTile/ProductTileToolbar';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { CartItem } from '@gik/core/models/gik/Order';
import {
  CheckoutLauncher,
  createCartItemFromProduct,
  productHasIntegratedCheckout,
  productIsTangoCard,
} from '@gik/checkout';
import { navigateTo } from '@/utils/environment';

export interface IRelatedProductsProps {
  inkindRouteId?: string;
  userId?: string;
  products?: Product[];
  skeletonItems?: number;
  className: string;
}

const skelBem = bemBlock(`related-products-skeleton`);
const Skeleton = ({ skeletonItems = 4 }: Partial<IRelatedProductsProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={10} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

const SlickButtonFix = ({ children, ...props }) => {
  delete props.currentSlide;
  delete props.slideCount;
  return <span {...props}>{children}</span>;
};

function RelatedProducts({ products, inkindRouteId, className }: IRelatedProductsProps): React.ReactElement {
  const bem = bemBlock('related-products');
  const blockClasses = classnames([bem(), 'gik-carousel-product-tiles', className || '']);

  function getSlidesToShow(slidesToShow = 4): number {
    if (slidesToShow > products?.length) {
      return products.length;
    }
    return slidesToShow;
  }

  const isLoading = !products;

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <Carousel
      className={blockClasses}
      dots={false}
      infinite={false}
      slidesToScroll={1}
      slidesToShow={getSlidesToShow(4)}
      arrowButtonSize="lg"
      responsive={[
        {
          breakpoint: parseInt(twConfig.theme.screens.xl),
          settings: {
            slidesToShow: getSlidesToShow(4),
          },
        },
        {
          breakpoint: parseInt(twConfig.theme.screens.lg),
          settings: {
            slidesToShow: getSlidesToShow(3),
          },
        },
        {
          breakpoint: parseInt(twConfig.theme.screens.md),
          settings: {
            slidesToShow: getSlidesToShow(2),
          },
        },
      ]}
      /*prevArrow={
        <SlickButtonFix>
          <Button
            className={bem('slick-button', [{ prev: true }])}
            circle
            variant="white"
            size={'base'}
            prepend={<SvgIcon name="ChevronLeft" />}
          />
        </SlickButtonFix>
      }
      nextArrow={
        <SlickButtonFix>
          <Button
            className={bem('slick-button', [{ next: true }])}
            circle
            variant="white"
            size={'base'}
            prepend={<SvgIcon name="ChevronRight" />}
          />
        </SlickButtonFix>
      }*/
    >
      {products?.map((product, index) => {
        return (
          <div key={index} className="gik-carousel-product-tile">
            <RelatedProductTile
              item={product}
              inkindRouteId={inkindRouteId}
              toolbar={<ProductTileToolbar itemId={product.id} inkindRouteId={inkindRouteId} />}
            />
          </div>
        );
      })}
    </Carousel>
  );
}

export default withSuspense(RelatedProducts, Skeleton);

interface IRelatedProductTileProps {
  item: Product;
  inkindRouteId: string;
  toolbar: React.ReactNode;
  direction?: ProductTileDirection;
}

function RelatedProductTile({ item, inkindRouteId, toolbar, direction = 'column' }: IRelatedProductTileProps) {
  const cartItem: CartItem = createCartItemFromProduct(item);
  const hasIntegratedCheckout: boolean = productHasIntegratedCheckout(item);
  const wishlistProductUrl = !hasIntegratedCheckout ? `/products/${item?.slug}` : null;

  return (
    <CheckoutLauncher
      key={item.id}
      initiatedOn="wishlist"
      cart={[cartItem]}
      cachedProducts={[item]}
      initialInkindRouteId={inkindRouteId}
      child={onClick => (
        <ProductTile
          containImage={productIsTangoCard(item)}
          imageUrl={item.thumbnails[0]}
          onClick={event => (wishlistProductUrl ? navigateTo(wishlistProductUrl) : onClick(event))}
          toolbar={toolbar}
          direction={direction}
          title={item.name}
        />
      )}
    />
  );
}
